import React, { useState } from 'react';
import Classes from './RecordTable.module.css';
import { useNavigate } from 'react-router-dom';
import MoreDropDown from './MoreDropDown';
import DropDown from './StatusDropDown/DropDown';
import { AnimatePresence } from 'framer-motion';
import UpdateStatusPopup from '../../../../../../GlobalComponents/UpdateStatusPopup/UpdateStatusPopup';
import backend from '../../../../../../../api';

const UI_TO_DB = {
  "Completed": "completed",
  "Upcoming": "upcoming",
  "Cancelled": "cancelled",
  "On the way": "on_the_way",
  "Delayed": "delayed",
};

// 🌍 Open address in Maps (works on desktop & mobile)
const openInMaps = (address) => {
  if (!address) return;
  const encoded = encodeURIComponent(address);
  const url = `https://www.google.com/maps/search/?api=1&query=${encoded}`;
  window.open(url, '_blank', 'noopener,noreferrer');
};

export default function TableRow({ value, index, isLast, isAppointments, booking, onStatusChanged }) {
  const navigateTo = useNavigate();
  const [openUpdateStatusPopup, setopenUpdateStatusPopup] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleCloseUpdateStatusPopup = () => setopenUpdateStatusPopup(false);
  const handleOpenUpdateStatusPopup = () => setopenUpdateStatusPopup(true);

  // ✅ US phone formatting helper: +1 (123)-456-7890
  const formatPhone = (phone) => {
    if (!phone) return '—';
    const digits = String(phone).replace(/\D/g, '');
    if (digits.length === 10) return `+1 (${digits.slice(0,3)})-${digits.slice(3,6)}-${digits.slice(6)}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+1 (${digits.slice(1,4)})-${digits.slice(4,7)}-${digits.slice(7)}`;
    return phone; // fallback for non-US
  };

  // Save to backend after dropdown changes
  const saveStatus = async (uiVal) => {
    if (!booking?._id) return;
    const status = UI_TO_DB[uiVal] || "upcoming";
    try {
      setSaving(true);
      const { data } = await backend.put(`/api/bookings/${booking._id}/status`, { status });
      if (data?.ok) onStatusChanged?.(uiVal);
    } catch (e) {
      alert(e?.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div
        className={`${Classes.customerRow}`}
        key={booking?._id || index}
        style={{ border: isLast && !isAppointments ? 'none' : '' }}
      >
        <div className={`${Classes.rowElem} ${Classes.elem1}`}><h5>{value.serviceDate}</h5></div>
        <div className={`${Classes.rowElem} ${Classes.elem2}`}><h5>{value.client}</h5></div>
        <div className={`${Classes.rowElem} ${Classes.elem3}`}><h5>{value.serviceType}</h5></div>
        <div className={`${Classes.rowElem} ${Classes.elem4}`}>
          <span onClick={handleOpenUpdateStatusPopup}>
            <DropDown
              selectedValue={value.status.label}
              options={["Completed", "Upcoming", "Cancelled", "On the way", "Delayed"]}
              onChange={saveStatus}
              isDropDown={true}
              disabled={saving}
            />
          </span>
        </div>

        {/* 🌍 Desktop address: clickable, opens Maps */}
        <div className={`${Classes.rowElem} ${Classes.elem5}`}>
          <h5
            className={value.address ? Classes.addressLink : ''}
            onClick={() => value.address && openInMaps(value.address)}
          >
            {value.address || '—'}
          </h5>
        </div>

        <div className={`${Classes.rowElem} ${Classes.elem6}`}>
          <h5>{formatPhone(value.phone)}</h5>
        </div>
        <div className={`${Classes.rowElem} ${Classes.elem7}`}>
          <MoreDropDown
            booking={booking}
            row={value}
            onView={() => navigateTo(`/view-appointment/details/${booking?._id}`)}
            onEdit={() => navigateTo(`/edit-appointment/${booking?._id}`)}
          />
        </div>
      </div>

      {/* Mobile */}
      <div className={Classes.customerRowMobile}>
        <div>
          <div className={Classes.mobElem}><h4>Service Date</h4><h5>{value.serviceDate}</h5></div>
          <div className={Classes.mobElem}><h4>Client</h4><h5>{value.client}</h5></div>
          <div className={Classes.mobElem}><h4>Service Type</h4><h5>{value.serviceType}</h5></div>
        </div>
        <div>
          <div className={Classes.mobElem}>
            <h4>Status</h4>
            <span onClick={handleOpenUpdateStatusPopup}>
              <DropDown
                selectedValue={value.status.label}
                options={["Completed", "Upcoming", "Cancelled", "On the way", "Delayed"]}
                onChange={saveStatus}
                isDropDown={true}
                disabled={saving}
              />
            </span>
          </div>

          {/* 🌍 Mobile address: also clickable */}
          <div className={Classes.mobElem}>
            <h4>Address</h4>
            <h5
              className={value.address ? Classes.addressLink : ''}
              onClick={() => value.address && openInMaps(value.address)}
            >
              {value.address || '—'}
            </h5>
          </div>

          <div className={Classes.mobElem}>
            <h4>Phone</h4>
            <h5>{formatPhone(value.phone)}</h5>
          </div>
        </div>
        <MoreDropDown
          booking={booking}
          row={value}
          onView={() => navigateTo(`/view-appointment/details/${booking?._id}`)}
          onEdit={() => navigateTo(`/edit-appointment/${booking?._id}`)}
        />
      </div>

      <AnimatePresence>
        {openUpdateStatusPopup && (
          <UpdateStatusPopup
            closePopup={handleCloseUpdateStatusPopup}
            submitPopup={() => {}}
            booking={booking}
            initialStatus={value.status.label}
            onSaved={(savedUiLabel) => {
              onStatusChanged?.(savedUiLabel);
              handleCloseUpdateStatusPopup();
            }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
